import { IconInfoTriangle } from '@tabler/icons-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { z } from 'zod';

import { DashboardSpecRenderer } from '#core/components/dashboard/spec/DashboardSpecRenderer.tsx';
import { LabeledLoader } from '#core/components/layout/LabeledLoader.tsx';
import { NO_POS } from '#core/lib/expr/ast.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { useEvalExpr } from '#core/lib/expr/session/useEvalExpr.ts';
import { type DashboardSpec, expectDashboardSpec } from '#core/lib/expr/value.ts';

import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import { AskUserQuestionTool } from '#plugin-chat/components/tools/ask-user-question/AskUserQuestionTool.tsx';
import { withMention } from '#plugin-chat/components/tools/helpers/withMention.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { ToolErrorMessage } from '#plugin-chat/lib/tool/message-components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

const ShowDashboardInputSchema = z.object({
  expression: z.string().min(1).describe('An s-expression whose top-level form is `(dashboard …)`.'),
});
type ShowDashboardInput = z.infer<typeof ShowDashboardInputSchema>;
type ShowDashboardOutput = {
  readonly tileCount: number;
  readonly tiles: readonly { readonly kind: string; readonly span: readonly [number, number] }[];
};

/**
 * Suspense-eval the dashboard expression to its `DashboardSpec`.
 * Evaluation is cheap + pure (the dashboard op builds tile specs
 * holding unresolved `ColumnRef`s — no CDF traffic). Per-tile data
 * resolution happens later, inside `<DashboardSpecRenderer>`. Keyed by
 * `session.id` + expression so sibling renders share the cache.
 */
function useDashboardSpec(expression: string): DashboardSpec {
  const evalExpr = useEvalExpr();
  const session = useSession();
  const { data } = useSuspenseQuery({
    queryKey: ['show-dashboard-spec', session.id, expression],
    queryFn: async () => expectDashboardSpec(await evalExpr(expression), 'showDashboard render', NO_POS),
    // Fail fast — deterministic eval; the default 3× retry + backoff
    // only suspends the dashboard for ~5s before the Error Boundary
    // trips. Mirrors `createChatSession.fetch` / `useMaterializedExpr`.
    retry: false,
  });
  return data;
}

function DashboardResult({ expression }: { expression: string }) {
  return <DashboardSpecRenderer spec={useDashboardSpec(expression)} />;
}

/**
 * ShowDashboardTool — s-expr-driven dashboard.
 *
 * The LLM writes an s-expression whose top-level form is
 * `(dashboard (metric …) (cartesian …) (table …))`. `execute`
 * evaluates it (cheap, no I/O — tile specs carry unresolved
 * `ColumnRef`s) and returns a tile-summary envelope so the LLM knows
 * the dashboard rendered. `render` re-evaluates (shared Suspense
 * cache) and hands the `DashboardSpec` to the V-shape-native
 * `<DashboardSpecRenderer>`, which materialises each tile's columns on
 * demand with per-tile Suspense + error isolation.
 *
 * No factory props / no `outputDataset` binding — `<ChatSessionProvider>`
 * populates `EvalDepsContext` once for the whole conversation; the
 * render-side hook reads the Session via `useSession`/`useEvalExpr`.
 *
 * The full LLM-facing usage guidance dropped during the data-pipeline →
 * s-expr migration (ref-resolution discipline, KPI binding, chart-type
 * selection, the distinct-column rule, reference lines, binary/status
 * handling, long-format pivoting) is restored on the `description` prop
 * via a nested `<ToolGuidance>` — the new `<Tool>` has no separate
 * `instructions` channel. The DSL *grammar* is documented globally in
 * the shared `<ExprDslReference>`; this guidance is strategic/semantic
 * only and is recast against the dashboard s-expr constructs (no
 * step-curve type exists; scatter is `:type dot`).
 */
export const ShowDashboardTool = withMention('showDashboard', function ShowDashboardToolImpl() {
  const evalExpr = useEvalExpr();

  return (
    <Tool<ShowDashboardInput, ShowDashboardOutput>
      description={
        <ToolGuidance
          description="Generate a dashboard (charts, tables, KPIs) to present results to the user."
          tool={ShowDashboardTool}
          whenToUse={[
            'Display results/datasets from other tool calls (`queryKnowledgeGraph`, `transformDataset`)',
            'Display knowledge graph data (table or metric tile)',
            'Display timeseries data (line/area/bar/dot charts)',
            'Display a single-number answer (a `metric` tile)',
          ]}
        >
          <p>
            <strong>Always use {ShowDashboardTool.mention} to present results to the user</strong> — including data from{' '}
            <code>queryKnowledgeGraph</code> and <code>transformDataset</code>. Never type rows as a markdown/bullet
            list or inline table; that is a hard failure mode. Prose is reserved for the narrative around the dashboard
            (question, insight, next steps), never the rows themselves. When a single value is the answer (e.g. a
            count), render it as a <code>metric</code> tile, not a sentence.
          </p>

          <h3>Tile structure &amp; layout</h3>
          <p>
            The top-level form is <code>(dashboard &lt;tile&gt; &lt;tile&gt; …)</code>. Tiles are positional children
            rendered left-to-right, top-to-bottom in declaration order on a fixed 12-column grid. Every tile takes{' '}
            <code>:span [W H]</code> (each 1–12). The tile forms are <code>(metric …)</code>, <code>(cartesian …)</code>{' '}
            (charts), and <code>(table (column …) …)</code>. Reference a staged dataset column with{' '}
            <code>(col &lt;dataset&gt; &lt;column&gt;)</code>.
          </p>

          <h3>Resolve references before display</h3>
          <p>
            <strong>
              Every column a tile binds ( <code>:value</code>, <code>:y</code>, table <code>:ref</code> ) MUST be a
              resolved scalar or numeric column — NEVER a raw timeseries reference.
            </strong>{' '}
            A <code>ref&lt;timeseries&gt;</code> column holds no values; passing it to a tile renders an opaque id
            string (<code>cdf:ts:…</code>) and the output is broken. Produce the resolved column in{' '}
            <code>transformDataset</code> first (a <code>derive</code> of the latest value, or a{' '}
            <code>time-bucket</code> for a chart series), bind that dataset, then point the tile at it.
          </p>

          <h3>Single-number answers &amp; KPIs</h3>
          <p>
            A <code>metric</code> tile is dataset-driven:{' '}
            <code>(metric :variant kpi :value (col &lt;ds&gt; &lt;col&gt;) :label "…" :format compact)</code>. For a
            single-number answer (e.g. "how many wells in field X"), end the pipeline with a{' '}
            <code>transformDataset</code> aggregate producing a 1-row dataset (e.g.{' '}
            <code>(transform &lt;wells&gt; (aggregate (total_wells (count))))</code>) and point <code>:value</code> at{' '}
            <code>(col totals total_wells)</code>. For a value-with-trend KPI, additionally bind{' '}
            <code>:delta (col &lt;ds&gt; &lt;deltaCol&gt;)</code> — it drives the change/trend indicator.
          </p>

          <h3>Human-readable labels</h3>
          <p>
            Always supply an explicit <code>:label</code> on every <code>metric</code>, <code>series</code>, and table{' '}
            <code>column</code> — e.g. <code>:label "SE0074 Long – Avg Priority Oil Rate (Sm³/d)"</code>. Raw column
            identifiers are not acceptable user-facing labels.
          </p>

          <p>
            <strong>
              Always label both the X axis and the Y axis. Exclude unit on the axis, include it as a kwarg on series
              instead.
            </strong>
          </p>

          {/* <h3>Chart-type selection</h3> */}
          {/* <dl> */}
          {/*   <dt> */}
          {/*     <code>(series :type line …)</code> / <code>:type area</code> */}
          {/*   </dt> */}
          {/*   <dd>Trends over time</dd> */}
          {/*   <dt> */}
          {/*     <code>:type bar</code> */}
          {/*   </dt> */}
          {/*   <dd>Part-to-whole comparison (6 or fewer categories)</dd> */}
          {/*   <dt> */}
          {/*     <code>:type dot</code> */}
          {/*   </dt> */}
          {/*   <dd> */}
          {/*     Correlations / scatter (there is no scatter/bubble type — use <code>dot</code>) */}
          {/*   </dd> */}
          {/* </dl> */}
          {/* <p> */}
          {/*   <strong>Each series must map to a distinct column.</strong> Two <code>series</code> in one{' '} */}
          {/*   <code>cartesian</code> cannot both bind the same <code>:y (col …)</code> — duplicate column refs collapse */}
          {/*   into a single line and produce a broken render. To overlay related metrics, point each series at its own */}
          {/*   column (they may even come from different bound datasets sharing an x column). */}
          {/* </p> */}

          <h3>Reference lines</h3>
          <p>
            Add a <code>(reference-line :axis y :value &lt;n&gt; :label "…" :style dashed)</code> child to a{' '}
            <code>cartesian</code> when the data context has known limits, targets, regulatory thresholds, safety
            boundaries, or computed averages/medians. Use <code>:axis y</code> for horizontal lines (numeric thresholds)
            and <code>:axis x</code> for vertical lines (event dates/categories) — these add critical context that makes
            a chart immediately actionable.
          </p>

          {/* <h3>Binary and status data</h3> */}
          {/* <p> */}
          {/*   The series DSL has <strong>no step-curve type</strong>. For binary/status data (0/1 like online/offline), */}
          {/*   humanize the values upstream in <code>transformDataset</code> (derive a label column mapping 0→"Offline", */}
          {/*   1→"Online") and chart it as <code>:type line</code> or <code>:type bar</code>; label the series and caption */}
          {/*   with the human terms, not "1 = online, 0 = offline". */}
          {/* </p> */}

          {/* <h3>Long-format data: pivot before charting</h3> */}
          {/* <p> */}
          {/*   If the dataset has <em>one row per (x, group)</em> with a discriminator column identifying the group (rows */}
          {/*   shaped <code>{'{ timestamp, series, rate }'}</code> where <code>series</code> is the entity id), it is{' '} */}
          {/*   <strong>long format</strong> and cannot be charted directly — every chart series would have to bind the same{' '} */}
          {/*   <code>rate</code> column, violating the distinct-column rule. Reshape to <strong>wide format</strong> (one */}
          {/*   row per x, one column per group) with a <code>transformDataset</code> <code>(… (pivot …))</code> first, then */}
          {/*   point each <code>series</code> at one of the resulting per-group columns. */}
          {/* </p> */}

          <example name="transform-to-cartesian-dashboard">
            <p>User: "Plot daily revenue vs. cost for 2026."</p>
            <ol>
              <li>
                <code>transformDataset</code> — bucket the raw rows to a daily grain (<code>time-bucket</code>),
                outputDataset <code>daily-fin</code> → binds <code>daily-fin</code>
              </li>
              <li>
                <code>showDashboard</code> — expression{' '}
                <code>
                  (dashboard (cartesian (series :type line :id rev :label "Revenue" :color blue :x (col daily-fin day)
                  :y (col daily-fin revenue)) (series :type line :id cost :label "Cost" :color red :x (col daily-fin
                  day) :y (col daily-fin cost)) (reference-line :axis y :value 0 :label "Break-even" :style dashed)
                  :span [12 6]))
                </code>
              </li>
            </ol>
          </example>
        </ToolGuidance>
      }
      display={{
        running: 'Building dashboard...',
        complete: { label: 'Dashboard rendered', semantic: 'view-query' },
      }}
      execute={async (args: ShowDashboardInput): Promise<ShowDashboardOutput> => {
        // Eval is cheap + pure — no CDF traffic. The tile-summary
        // envelope lets the LLM confirm the dashboard rendered without
        // forcing per-tile materialisation.
        const value = await evalExpr(args.expression);
        const spec = expectDashboardSpec(value, 'showDashboard result', NO_POS);
        return {
          tileCount: spec.tiles.length,
          tiles: spec.tiles.map((t) => ({ kind: t.kind, span: t.span })),
        };
      }}
      message={{
        call: ({ args }) => (
          <p>
            Calling <code>showDashboard</code> to display a dashboard with expression <code>{args.expression}</code>...
          </p>
        ),
        result: ({ output }) => (
          <>
            <p>
              The <code>showDashboard</code> tool call completed successfully. The dashboard has been rendered above
              this message ({output.tileCount} tile{output.tileCount === 1 ? '' : 's'}:{' '}
              {output.tiles.map((t) => t.kind.replace('-spec', '')).join(', ')}). The visual deliverable is complete.
            </p>
            <p>
              Proceed to ask the user follow-up questions using {AskUserQuestionTool.mention}, or provide a concise
              summary if no follow-up is needed.
            </p>
          </>
        ),
        error: ({ envelope, toolName }) => (
          <ToolErrorMessage
            envelope={envelope}
            recovery={
              <p>
                The expression must evaluate to <code>(dashboard …)</code>. Ensure every <code>(col …)</code> names a
                staged dataset (run its producing tool first), then retry.
              </p>
            }
            toolName={toolName}
          />
        ),
      }}
      name="showDashboard"
      parameters={ShowDashboardInputSchema}
      render={({ args, ...props }) => (
        <ToolDisplay>
          <ToolDisplay.Content>
            {props.status.type === 'complete' && args.expression && (
              <ErrorBoundary fallbackRender={() => <div>Failed to render dashboard</div>}>
                <Suspense fallback={<div>Building dashboard…</div>}>
                  {args.expression && <DashboardResult expression={args.expression} />}
                </Suspense>
              </ErrorBoundary>
            )}
            {props.status.type === 'running' && <LabeledLoader label="Preparing dashboard…" />}
          </ToolDisplay.Content>
          <ToolDisplay.Error color="var(--mantine-color-orange-4)" icon={IconInfoTriangle}>
            An error occurred while displaying the dashboard.
          </ToolDisplay.Error>
        </ToolDisplay>
      )}
    />
  );
});
